import { Loader, Modal } from '@mantine/core'
import { Check } from 'lucide-react'

import Button from 'src/components/Buttons/Button/Button'
import { VerifiedCallerIdStatus } from 'src/graphql/types/verifiedCallerIds'
import { displayPhoneNumber } from 'src/lib/phone.utils'
import { cn } from 'src/utils'

import CallerIdShield, { callerIdStatusLabel } from './CallerIdShield'
import type { VerifiedCallerIdRow } from './useVerifiedCallerId'

interface CallerIdVerificationModalProps {
  opened: boolean
  onClose: () => void
  phoneNumber: string
  /** Whose calls this number will appear on, for the copy. */
  caller: 'you' | 'they' | 'your team'
  currentRow?: VerifiedCallerIdRow
  staleRow?: VerifiedCallerIdRow
  canManage: boolean
  busy: boolean
  onRequest: () => void
  onRemove: (row: VerifiedCallerIdRow) => void
  /** Re-reads the status from the server (used by Done). */
  onRefresh: () => void
}

type StepState = 'todo' | 'active' | 'done' | 'failed'

const POSSESSIVE: Record<CallerIdVerificationModalProps['caller'], string> = {
  you: 'Your',
  they: 'Their',
  'your team': "Your team's",
}

// One text style for everything in the modal body; only the step markers
// carry state.
const TEXT = 'text-sm text-doubleNickel-gray-700'

const Step = ({
  number,
  title,
  state,
  children,
}: {
  number: number
  title: string
  state: StepState
  children?: React.ReactNode
}) => (
  <li className="flex flex-row gap-3">
    <div
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
        state === 'done' && 'bg-doubleNickel-success-500 text-white',
        state === 'active' && 'bg-doubleNickel-brand-500 text-white',
        state === 'failed' && 'bg-doubleNickel-error-500 text-white',
        state === 'todo' &&
          'border border-doubleNickel-gray-300 text-doubleNickel-gray-500'
      )}
    >
      {state === 'done' ? <Check size={12} /> : number}
    </div>
    <div className="flex flex-1 flex-col gap-2 pt-0.5">
      <div className={TEXT}>{title}</div>
      {children}
    </div>
  </li>
)

/**
 * Walks through verifying a phone number as an outbound caller ID. Twilio
 * calls the number as soon as verification starts; the person answering keys
 * in the code shown here. Closing the modal never cancels a call in flight;
 * the status icon keeps updating on its own.
 */
const CallerIdVerificationModal = ({
  opened,
  onClose,
  phoneNumber,
  caller,
  currentRow,
  staleRow,
  canManage,
  busy,
  onRequest,
  onRemove,
  onRefresh,
}: CallerIdVerificationModalProps) => {
  const formatted = displayPhoneNumber(phoneNumber) ?? phoneNumber
  const possessive = POSSESSIVE[caller]
  // Twilio calls the number being verified, which is only "me" for the
  // viewer's own phone.
  const callLabel = caller === 'you' ? 'Call me' : 'Call this number'
  const status = currentRow?.status ?? null
  const isVerified = status === VerifiedCallerIdStatus.VERIFIED
  const isPending = status === VerifiedCallerIdStatus.PENDING
  const isFailed = status === VerifiedCallerIdStatus.FAILED

  const callStep: StepState = isPending ? 'done' : 'todo'
  const codeStep: StepState = isPending
    ? 'active'
    : isFailed
    ? 'failed'
    : 'todo'

  const handleDone = () => {
    onRefresh()
    onClose()
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="md"
      classNames={{ title: 'font-medium' }}
      title="Caller ID Verification"
      centered
    >
      <div className="flex flex-col gap-5">
        <div
          className={cn(
            'flex flex-row items-center gap-2 rounded-md border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-3 py-2',
            TEXT
          )}
        >
          <CallerIdShield status={status} />
          <span>{formatted}</span>
          <span>· {callerIdStatusLabel(status)}</span>
        </div>

        {isVerified ? (
          <p className={TEXT}>
            {possessive} calls to applicants show {formatted}. Applicants who
            call or text back reach this phone directly, not Double Nickel.
          </p>
        ) : (
          <>
            <p className={TEXT}>
              Once verified, {possessive.toLowerCase()} calls to applicants show{' '}
              {formatted} instead of the Double Nickel number.
            </p>

            <ol className="flex flex-col gap-4">
              <Step
                number={1}
                title="Double Nickel calls this number"
                state={callStep}
              />

              <Step
                number={2}
                title="Answer and enter the code shown here"
                state={codeStep}
              >
                {isPending && (
                  <>
                    <div className="flex flex-row items-center gap-3">
                      <div className="rounded-md border border-doubleNickel-gray-200 bg-white px-4 py-2 font-mono text-2xl font-semibold tracking-[0.35em] text-doubleNickel-gray-900">
                        {currentRow.validationCode}
                      </div>
                      <Loader size="xs" />
                      <span className={TEXT}>Calling now</span>
                    </div>
                    {canManage && (
                      <button
                        type="button"
                        className="self-start text-sm text-doubleNickel-brand-500 hover:underline disabled:opacity-50"
                        onClick={onRequest}
                        disabled={busy}
                      >
                        {caller === 'you'
                          ? "Didn't get the call? Call again"
                          : 'Call again'}
                      </button>
                    )}
                  </>
                )}
                {isFailed && (
                  <p className="text-sm text-doubleNickel-error-500">
                    {currentRow.failureReason ??
                      'The code was not entered. Start again to get a new call.'}
                  </p>
                )}
              </Step>

              <Step
                number={3}
                title={`${possessive} calls show ${formatted}`}
                state="todo"
              />
            </ol>

            {staleRow && (
              <p className={TEXT}>
                {displayPhoneNumber(staleRow.phoneNumber)} was verified earlier
                and is still on file.{' '}
                {canManage && (
                  <button
                    type="button"
                    className="text-doubleNickel-brand-500 hover:underline disabled:opacity-50"
                    onClick={() => onRemove(staleRow)}
                    disabled={busy}
                  >
                    Remove it
                  </button>
                )}
              </p>
            )}
          </>
        )}

        <div className="flex w-full flex-row gap-3 border-t pt-4">
          {isVerified || isPending ? (
            <>
              {canManage && (
                <Button
                  variant="outline"
                  text={
                    isPending ? 'Cancel verification' : 'Remove verification'
                  }
                  className="flex-1"
                  disabled={busy}
                  onClick={() => onRemove(currentRow)}
                />
              )}
              <Button text="Done" className="flex-1" onClick={handleDone} />
            </>
          ) : (
            <>
              <Button
                variant="outline"
                text="Cancel"
                className="flex-1"
                onClick={onClose}
              />
              {canManage && (
                <Button
                  text={`${callLabel} ${isFailed ? 'again' : 'now'}`}
                  className="flex-1"
                  disabled={busy}
                  onClick={onRequest}
                />
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default CallerIdVerificationModal
